import { MediaTrackerFn } from '@/hooks/use-media-tracker/types';
import { EventProperties } from '@calm/evexia-analytics';
import { SECOND_MS } from '@calm/web-common/dist/time';
import { useAudio } from '@rhg/hooks/use-audio';
import { useGuideQueue } from '@rhg/hooks/use-guide-queue';
import { useMediaTracker } from '@rhg/hooks/use-media-tracker';
import { AnalyticsEventPropsProvider } from '@rhg/libs/analytics/event-props-provider';
import { LoopMode } from '@rhg/providers/guide-queue/types';
import {
    MediaEvent,
    NORMAL_PLAYBACK_RATE,
    toPercentCompleted,
} from '@rhg/utils/media';
import { useCallback, useEffect, useMemo } from 'react';
import { AudioControls } from './controls';
import { AudioMinimalControls } from './minimal-controls';
import {
    AudioPlayerControls,
    AudioPlayerProps,
    AudioPlayerVariant,
} from './types';

export function AudioPlayer({
    audio,
    title,
    subtitle,
    image,
    customAction,
    supportsAddToPlaylist,
    onAddToPlaylist,
    controls,
    supportsPlaybackSpeed = true,
    areControlsHidden,
    drawerTitleIcon,
    variant,
    autoplay,
    loop,
    previousProgress = 0,
    isInitiallyMuted,
    isFloating,
    willTrackProgress,
    onClose,
    trackFn,
    controlsSx,
    sx,
    onNextTrack,
    onPreviousTrack,
    onControlClick,
    onPlaybackMenuOpenChange,
}: AudioPlayerProps) {
    const {
        loadAudio,
        setLoop,
        mediaEl,
        isLoaded,
        controls: { setTime, setPlaybackRate, playbackRate },
    } = useAudio();

    const { loopMode } = useGuideQueue();

    /**
     * Ensures we don't display the same text twice in the UI, which can happen when the title and subtitle are the same.
     */
    const dedupedSubtitle = title === subtitle ? undefined : subtitle;

    // Supply live player state to button events emitted inside the player.
    // This component already re-renders on every audio tick, so reading here
    // adds no new re-render; the memoized provider value only changes when the
    // rate changes or the playhead crosses a 1% step (toPercentCompleted
    // rounds to hundredths of the fraction). Merged on top of the
    // ambient bundle from GuidePlayer. PlaybackSpeed is omitted entirely when
    // speed isn't supported; PercentCompleted is omitted until the media has
    // a duration (the GuidePlayer bundle's saved-position value then applies).
    const livePercentCompleted = mediaEl?.duration
        ? toPercentCompleted(mediaEl.currentTime, mediaEl.duration)
        : undefined;
    const liveAnalyticsAmbient = useMemo<EventProperties>(() => {
        const props: EventProperties = {};
        if (supportsPlaybackSpeed) {
            props.PlaybackSpeed = `${playbackRate}x`;
        }
        if (livePercentCompleted != null) {
            props.PercentCompleted = livePercentCompleted;
        }
        return props;
    }, [supportsPlaybackSpeed, playbackRate, livePercentCompleted]);

    const shouldLoop =
        !!loop ||
        loopMode === LoopMode.One ||
        controls === AudioPlayerControls.Soundscape;

    const trackerFn: MediaTrackerFn = useCallback(
        (progress) => {
            if (
                progress.eventType === MediaEvent.LoadedMetadata &&
                previousProgress
            ) {
                setTime(previousProgress / SECOND_MS);
            }
            trackFn?.(progress);
        },
        [previousProgress, setTime, trackFn]
    );

    useMediaTracker({
        assetType: 'audio',
        mediaEl,
        trackFn: trackerFn,
        willTrackProgress,
    });

    useEffect(() => {
        if (audio && !isLoaded) {
            if (!supportsPlaybackSpeed) {
                setPlaybackRate(NORMAL_PLAYBACK_RATE);
            }
            loadAudio({
                autoplay,
                loop: shouldLoop,
                audio,
                isInitiallyMuted,
            });
        }
    }, [
        audio,
        autoplay,
        isInitiallyMuted,
        isLoaded,
        loadAudio,
        onClose,
        setPlaybackRate,
        shouldLoop,
        supportsPlaybackSpeed,
    ]);

    useEffect(() => {
        if (isLoaded) {
            setLoop(shouldLoop);
        }
    }, [isLoaded, setLoop, shouldLoop]);

    // Varied controls
    return (
        <AnalyticsEventPropsProvider props={liveAnalyticsAmbient}>
            {variant !== AudioPlayerVariant.Minimal ? (
                <AudioControls
                    sx={sx}
                    variant={variant}
                    controls={controls}
                    supportsPlaybackSpeed={supportsPlaybackSpeed}
                    controlsSx={controlsSx}
                    title={title}
                    subtitle={dedupedSubtitle}
                    image={image}
                    customAction={customAction}
                    supportsAddToPlaylist={supportsAddToPlaylist}
                    onAddToPlaylist={onAddToPlaylist}
                    isFloating={isFloating}
                    onStop={onClose}
                    onNextTrack={onNextTrack}
                    onPreviousTrack={onPreviousTrack}
                    areControlsHidden={areControlsHidden}
                    onControlClick={onControlClick}
                    drawerTitleIcon={drawerTitleIcon}
                    onPlaybackMenuOpenChange={onPlaybackMenuOpenChange}
                />
            ) : (
                <AudioMinimalControls
                    sx={sx}
                    controlsSx={controlsSx}
                    title={title}
                    subtitle={dedupedSubtitle}
                    image={image}
                    isFloating={isFloating}
                    onStop={onClose}
                    controls={controls}
                    areControlsHidden={areControlsHidden}
                    onControlClick={onControlClick}
                />
            )}
        </AnalyticsEventPropsProvider>
    );
}
